import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Link2, FileDown, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { WhatsAppIcon, TelegramIcon, XIcon } from "./SocialIcons";

export const ShareInvoice = ({ invoice, creator, theme }) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const fromName = creator?.business_name || creator?.name || "a creator";
  const amount = `${invoice.currency} ${Number(invoice.total).toFixed(2)}`;
  const message = `Invoice ${invoice.number} from ${fromName} — ${amount}. Pay here: ${url}`;

  const waHref = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const tgHref = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(
    `Invoice ${invoice.number} from ${fromName} — ${amount}`
  )}`;
  const xText = `Invoice ${invoice.number} from ${fromName} — ${amount}. Drafted with Quill.`;
  const xHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(xText)}&url=${encodeURIComponent(url)}`;
  const liHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      toast.success("Invoice link copied");
      setTimeout(() => setCopiedLink(false), 1800);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className={`mt-12 border-t pt-8 ${theme.rule}`}
      data-testid="share-invoice-section"
    >
      <div className={`${theme.sectionLabel} mb-4`}>Share this invoice</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <a
          href={xHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-x-btn"
          className={`inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm transition-colors ${theme.ghostBtn}`}
        >
          <XIcon size={14} /> X
        </a>
        <a
          href={liHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-linkedin-btn"
          className={`inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm transition-colors ${theme.ghostBtn}`}
        >
          <Linkedin size={14} /> LinkedIn
        </a>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-whatsapp-btn"
          className={`inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm transition-colors ${theme.ghostBtn}`}
        >
          <WhatsAppIcon size={16} /> WhatsApp
        </a>
        <a
          href={tgHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-telegram-btn"
          className={`inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm transition-colors ${theme.ghostBtn}`}
        >
          <TelegramIcon size={14} /> Telegram
        </a>
        <a
          href={`${API}/public/invoices/${invoice.invoice_id}/pdf`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-pdf-btn"
          className={`inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm transition-colors ${theme.ghostBtn}`}
        >
          <FileDown size={14} /> PDF
        </a>
        <button
          onClick={copyLink}
          data-testid="share-copy-link-btn"
          className={`inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm transition-colors ${theme.ghostBtn}`}
        >
          {copiedLink ? <Check size={14} className="text-emerald-400" /> : <Link2 size={14} />}
          {copiedLink ? "Copied" : "Copy link"}
        </button>
      </div>
    </motion.div>
  );
};
